<template>
    <div class="relative w-full" ref="rootRef">
        <button
            type="button"
            :id="id || undefined"
            @click="open = !open"
            class="w-full h-14 rounded-lg px-4 text-sm font-bold text-gray-700 flex items-center justify-between gap-2 focus:border-hu-green focus:ring-1 focus:ring-hu-green appearance-none border"
            :class="hasError ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-200'"
            :aria-expanded="open"
            aria-haspopup="listbox"
        >
            <div class="flex items-center gap-2.5 min-w-0">
                <img
                    v-if="selectedFlag"
                    :src="selectedFlag"
                    :alt="selected?.plateKey || selected?.code"
                    class="h-5 w-5 rounded-full object-cover flex-shrink-0"
                />
                <span
                    v-else-if="selected && selected.code === 'OTHER'"
                    class="material-symbols-outlined text-hu-green text-[22px] flex-shrink-0 leading-none"
                    aria-hidden="true"
                >public</span>
                <span class="truncate text-left text-gray-500" :class="{ 'text-gray-700': selected }">{{ triggerLabel }}</span>
            </div>
            <span class="material-symbols-outlined text-gray-400 flex-shrink-0 pointer-events-none">expand_more</span>
        </button>
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-show="open"
                class="absolute z-20 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-60 overflow-hidden"
            >
                <div
                    v-if="searchable"
                    class="p-2 border-b border-gray-100 flex-shrink-0"
                    @click.stop
                >
                    <input
                        ref="searchInputRef"
                        v-model="searchQuery"
                        type="search"
                        autocomplete="off"
                        :placeholder="searchPlaceholder"
                        class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:border-hu-green focus:ring-1 focus:ring-hu-green outline-none transition-colors"
                        @keydown.escape.prevent="open = false"
                    />
                </div>
                <div class="overflow-y-auto py-1 min-h-0 flex-1">
                    <button
                        v-for="opt in filteredOptions"
                        :key="opt.code === '' ? '__empty__' : opt.code"
                        type="button"
                        class="w-full flex items-center gap-2.5 px-4 py-3 text-left text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                        :class="{ 'bg-hu-green/10 text-hu-green': modelValue === opt.code }"
                        @click="select(opt)"
                    >
                        <img
                            v-if="opt.flag"
                            :src="opt.flag"
                            :alt="opt.plateKey || opt.code"
                            class="h-5 w-5 rounded-full object-cover flex-shrink-0"
                        />
                        <span
                            v-else-if="opt.code === 'OTHER'"
                            class="material-symbols-outlined text-hu-green text-[22px] flex-shrink-0 leading-none"
                            aria-hidden="true"
                        >public</span>
                        <span v-else class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                        <span class="truncate" :class="{ 'text-gray-400 font-medium': opt.code === '' }">{{ displayLabel(opt) }}</span>
                    </button>
                    <p
                        v-if="filteredOptions.length === 0"
                        class="px-4 py-3 text-sm text-gray-500 text-center"
                    >
                        {{ noResultsText }}
                    </p>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
const FLAG_CDN = 'https://flagcdn.com/24x18';

export default {
    name: 'CheckValidityCountrySelect',
    props: {
        modelValue: { type: String, default: '' },
        options: {
            type: Array,
            default: () => [
                { code: 'CZ', label: 'Česko', flag: `${FLAG_CDN}/cz.png` },
                { code: 'DE', label: 'Germany', flag: `${FLAG_CDN}/de.png` },
                { code: 'AT', label: 'Austria', flag: `${FLAG_CDN}/at.png` },
                { code: 'SK', label: 'Slovakia', flag: `${FLAG_CDN}/sk.png` },
                { code: 'PL', label: 'Poland', flag: `${FLAG_CDN}/pl.png` }
            ]
        },
        placeholder: { type: String, default: 'Select country' },
        /** Associates outer wrapper for label[for] (button gets -trigger suffix) */
        id: { type: String, default: '' },
        searchable: { type: Boolean, default: true },
        searchPlaceholder: { type: String, default: 'Search...' },
        noResultsText: { type: String, default: 'No matches' },
        hasError: { type: Boolean, default: false }
    },
    emits: ['update:modelValue'],
    data() {
        return { open: false, searchQuery: '' };
    },
    computed: {
        filteredOptions() {
            const q = (this.searchQuery || '').trim().toLowerCase();
            if (!q) return this.options;
            return this.options.filter((opt) => {
                const label = this.displayLabel(opt).toLowerCase();
                const raw = String(opt.label || '').toLowerCase();
                const code = String(opt.code || '').toLowerCase();
                const plateKey = String(opt.plateKey || '').toLowerCase();
                return (
                    label.includes(q) ||
                    raw.includes(q) ||
                    code.includes(q) ||
                    plateKey.includes(q)
                );
            });
        },
        selected() {
            if (this.modelValue === '' || this.modelValue == null) return null;
            return this.options.find(o => o.code === this.modelValue) ?? null;
        },
        selectedFlag() {
            return this.selected?.flag ?? null;
        },
        triggerLabel() {
            if (!this.selected) return this.placeholder;
            return this.displayLabel(this.selected) || this.placeholder;
        }
    },
    watch: {
        open(val) {
            if (val) {
                this.$nextTick(() => {
                    const el = this.$refs.searchInputRef;
                    if (this.searchable && el) el.focus();
                });
            } else {
                this.searchQuery = '';
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.onOutsideClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onOutsideClick);
    },
    methods: {
        /** Show only country name: remove leading code (e.g. "DE Germany" → "Germany") and trailing (XX) */
        displayLabel(opt) {
            if (!opt || !opt.label) return '';
            let s = String(opt.label).trim();
            s = s.replace(/^[A-Za-z]{2,3}[\s\-]+/i, '').trim();
            s = s.replace(/\s*\([A-Za-z]{2,3}\)\s*$/i, '').trim();
            return s || opt.label;
        },
        onOutsideClick(e) {
            if (this.$refs.rootRef && !this.$refs.rootRef.contains(e.target)) this.open = false;
        },
        select(opt) {
            this.$emit('update:modelValue', opt.code);
            this.open = false;
        }
    }
};
</script>
